import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, UserSearch, Loader2 } from 'lucide-react';
import { PageHeader } from '../components/Scaffold';
import api from '../api/client';
import { messageFromDetail } from '../utils/errors';

/**
 * Cross-tenant identity search.
 *
 * Finds a person (any role -- client, admin, superadmin, etc.) by name,
 * email, or phone across EVERY café at once — each café has its own isolated database,
 * so this is normally the one place that can answer "every café this person
 * has touched" (fraud / support investigations). Reads the global `users`
 * table + `user_cafe_map` (already records every café a user has been
 * bound to).
 *
 * Endpoint: GET /api/internal/cross-tenant-search?q=... (superadmin-only,
 * min 2 chars). Results cap at 50 matches server-side.
 */
const MIN_QUERY_LENGTH = 2;

const lookup = async (q) => {
  const res = await api.get('/internal/cross-tenant-search', { params: { q } });
  return res.data || [];
};

export default function CrossTenantSearch() {
  // ?q= runs a search on arrival — the header search links here.
  const [searchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') || '';
  const arrivalQuery = urlQuery.trim();
  const arrives = arrivalQuery.length >= MIN_QUERY_LENGTH;

  const [query, setQuery] = useState(urlQuery);
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(arrives);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  // A search typed into this page's own box.
  const search = async (term) => {
    const q = term.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      setError(`Enter at least ${MIN_QUERY_LENGTH} characters.`);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      setResults(await lookup(q));
      setSearched(true);
    } catch (err) {
      console.error('Cross-tenant search failed:', err);
      setError(messageFromDetail(err, 'Search failed.'));
    } finally {
      setIsLoading(false);
    }
  };

  const runSearch = (e) => {
    e?.preventDefault();
    return search(query);
  };

  // The link changed while this page is already open (picking another person
  // from the header search): re-seed the box and loading flag while
  // rendering — React's "adjust state on prop change" pattern...
  const [appliedUrlQuery, setAppliedUrlQuery] = useState(urlQuery);
  if (appliedUrlQuery !== urlQuery) {
    setAppliedUrlQuery(urlQuery);
    setQuery(urlQuery);
    setError(null);
    setIsLoading(arrives);
  }

  // ...and run the lookup itself in an effect. `cancelled` drops the answer
  // if the link changes (or the page closes) before it arrives.
  useEffect(() => {
    if (!arrives) return undefined;
    let cancelled = false;
    lookup(arrivalQuery)
      .then((rows) => {
        if (cancelled) return;
        setResults(rows);
        setSearched(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Cross-tenant search failed:', err);
        setError(messageFromDetail(err, 'Search failed.'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [arrives, arrivalQuery]);

  return (
    <div className="page cross-tenant-search-page">
      <style>{`
        .cross-tenant-search-page {
          /* noLag rebrand: scoped to this page only. No Figma node exists
             for Cross-Tenant Search — applied by the same accent-only
             pattern as CafeOnboard/Licenses/Billing/Announcements/Support
             Tickets rather than verified against a design. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);
        }
      `}</style>
      <PageHeader title="Cross-Tenant Search" subtitle="Find a person by name, email, or phone across every café" />

      <form onSubmit={runSearch} className="search-bar" style={{ position: 'relative', marginBottom: 'var(--space-4)' }}>
        <Search size={16} className="search-bar__icon" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
        <input
          className="field__input"
          style={{ paddingLeft: 44 }}
          placeholder="Search name, email, or phone across all tenants…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </form>

      <div className="page-actions" style={{ marginBottom: 'var(--space-6)' }}>
        <button className="btn btn--primary" onClick={runSearch} disabled={isLoading}>
          {isLoading ? <Loader2 size={16} className="spin" /> : <Search size={16} />}
          {isLoading ? 'Searching…' : 'Search'}
        </button>
      </div>

      {error && (
        <div className="card" style={{ padding: 12, marginBottom: 12, color: 'var(--danger, #f87171)' }}>{error}</div>
      )}

      {searched && !error && (
        <div className="card table-card">
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Seen at</th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 ? (
                <tr><td colSpan={5} className="text-tertiary">No customers matched "{query.trim()}".</td></tr>
              ) : results.map((r) => (
                <tr key={r.id}>
                  <td><UserSearch size={14} /> {r.name}</td>
                  <td className="font-mono text-secondary">{r.email}</td>
                  <td className="font-mono text-secondary">{r.phone || '—'}</td>
                  <td className="text-tertiary">{r.role || '—'}</td>
                  <td className="text-tertiary">{r.cafes.length ? r.cafes.join(', ') : 'No café yet'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
